import React, { FC, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { useApolloClient } from '@apollo/client';
import { v4 as uuidv4 } from 'uuid';
import { GET_PRESIGNED_URL } from '../Popups/api/FileUploadQuery';
import { ALLOWED_MAP, CLASSROOM_S3_PATH, FETCH_POLICY_NO_CACHE, IMAGE_MAX_SIZE_MB, messages } from '../../utils/constants';
import ToasterWithMessage from '../toaster';
import './QuizImage.scss';

const QUIZ_IMAGE_FOLDER = 'quiz';
const QUIZ_IMAGE_TYPES = ALLOWED_MAP['Images'].mimes;

// A saved image is its S3 key; a newly picked image stays a File until the quiz is saved
export type QuizImageValue = string | File;

// Quiz images are stored as S3 keys (katon-classroom/quiz/...) and served from the frontend domain
export const getQuizImageUrl = (key: string): string => `${process.env.REACT_APP_FRONTEND_URL ?? ''}${key}`;

// Uploads one image to S3 through a presigned URL and returns its key.
// Uses the Apollo client directly so several uploads can run at the same time, each with its own URL.
export const useQuizImageUpload = () => {
    const client = useApolloClient();

    const uploadImage = async (file: File): Promise<string> => {
        const sanitizedName = file.name.replace(/[^a-zA-Z0-9._-]/g, '_');
        const key = `${CLASSROOM_S3_PATH}${QUIZ_IMAGE_FOLDER}/${uuidv4()}-${sanitizedName}`;
        const { data } = await client.query({
            query: GET_PRESIGNED_URL,
            variables: { input: { key } },
            fetchPolicy: FETCH_POLICY_NO_CACHE
        });
        const preSignedUrl = data?.getUploadPresignedUrl?.preSignedUrl;
        if (!preSignedUrl) throw new Error('Unable to get upload URL');
        await axios.put(preSignedUrl, file, { headers: { 'Content-Type': file.type } });
        return key;
    };

    return { uploadImage };
};

const ImageIcon: FC = () => (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <rect x="3" y="3" width="18" height="18" rx="2" />
        <circle cx="8.5" cy="8.5" r="1.5" />
        <path d="M21 15l-5-5L5 21" />
    </svg>
);

interface QuizImageButtonProps {
    hasImage: boolean;
    onSelect: (file: File) => void;
}

// Picks and validates an image; one image per field, a new one replaces it. Uploading happens when the quiz is saved.
export const QuizImageButton: FC<QuizImageButtonProps> = ({ hasImage, onSelect }) => {
    const inputRef = useRef<HTMLInputElement>(null);
    const { error } = ToasterWithMessage();

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;
        if (!QUIZ_IMAGE_TYPES.includes(file.type)) {
            error(messages.quizImageType);
            return;
        }
        if (file.size > IMAGE_MAX_SIZE_MB * 1024 * 1024) {
            error(messages.quizImageSize);
            return;
        }
        onSelect(file);
    };

    return (
        <>
            <button
                type="button"
                className="mathEditorBtn quizImageBtn"
                title={hasImage ? 'Replace image' : 'Add image'}
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => inputRef.current?.click()}
            >
                <ImageIcon />
            </button>
            <input ref={inputRef} type="file" accept={QUIZ_IMAGE_TYPES.join(',')} hidden onChange={handleFileChange} />
        </>
    );
};

interface QuizImagePreviewProps {
    image: QuizImageValue;
    onRemove: () => void;
    className?: string;
}

// Thumbnail shown to the teacher under the field it belongs to; a picked file is previewed locally
export const QuizImagePreview: FC<QuizImagePreviewProps> = ({ image, onRemove, className }) => {
    const [src, setSrc] = useState<string>();

    useEffect(() => {
        if (typeof image === 'string') {
            setSrc(getQuizImageUrl(image));
            return;
        }
        const objectUrl = URL.createObjectURL(image);
        setSrc(objectUrl);
        return () => URL.revokeObjectURL(objectUrl);
    }, [image]);

    return (
        <div className={`quizImagePreview ${className ?? ''}`}>
            {src && <img src={src} alt="Selected" />}
            <button type="button" className="quizImageRemove" title="Remove image" onClick={onRemove}>×</button>
        </div>
    );
};

interface QuizImageProps {
    imageKey?: string | null;
    alt: string;
}

// Image shown to students with the question or option it belongs to
export const QuizImage: FC<QuizImageProps> = ({ imageKey, alt }) => (
    imageKey ? <img className="quizImage" src={getQuizImageUrl(imageKey)} alt={alt} loading="lazy" /> : null
);
